/** One fountain assembly for streamed settlements and the authoring lab.
 * Pools, outlets and receiving basins share dimensions; water is a lit surface. */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { rngFromString } from '@hyperlight/shared';
export interface FountainBasin {
    radius: number;
    floor: number;
    waterY: number;
    rimY: number;
}
export interface FountainFlow {
    from: number;
    to: number;
    start: THREE.Vector3;
    end: THREE.Vector3;
}
export function makePoolWaterMat(time: {
    value: number;
}): THREE.MeshStandardMaterial {
    return waterMaterial(time, false);
}
function waterMaterial(time: {
    value: number;
}, stream: boolean): THREE.MeshStandardMaterial {
    const m = new THREE.MeshStandardMaterial({ color: stream ? 0xaecbc7 : 0x587f7b,
        roughness: stream ? .2 : .27, metalness: .05, transparent: true, opacity: stream ? .6 : .82,
        depthWrite: false, side: THREE.DoubleSide });
    m.onBeforeCompile = shader => {
        shader.uniforms.uWaterTime = time;
        shader.vertexShader = 'varying vec3 vWaterP;\nvarying vec2 vWaterUv;\n' + shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\nvWaterP=position; vWaterUv=uv;');
        shader.fragmentShader = 'uniform float uWaterTime;\nvarying vec3 vWaterP;\nvarying vec2 vWaterUv;\n' + shader.fragmentShader;
        // Perturb the lighting normal before PBR lighting, tone mapping and fog.
        // Keep contrast subtle: no emissive cyan, opaque volumes or full foam rims.
        shader.fragmentShader = shader.fragmentShader.replace('#include <normal_fragment_maps>', '#include <normal_fragment_maps>\n' + (stream
            ? 'normal=normalize(normal+vec3(0.025*sin(vWaterUv.x*80.0-uWaterTime*6.0),0.0,0.0));'
            : 'normal=normalize(normal+vec3(0.035*sin(vWaterP.x*8.0+uWaterTime*1.2),0.025*cos(vWaterP.z*9.0-uWaterTime),0.0));'));
    };
    m.customProgramCacheKey = () => `town-water-v3-${stream ? 'stream' : 'pool'}`;
    return m;
}
/** Closed cross-section with a recessed interior, rather than a solid disk. */
export function basinGeometry(radius: number, baseY: number): THREE.LatheGeometry {
    return new THREE.LatheGeometry([
        [0, baseY], [radius * .86, baseY], [radius + .18, baseY + .28],
        [radius + .18, baseY + .46], [radius, baseY + .46], [radius, baseY + .14],
        [0, baseY + .14], [0, baseY],
    ].map(p => new THREE.Vector2(p[0], p[1])), 32);
}
class SpillCurve extends THREE.Curve<THREE.Vector3> {
    constructor(readonly start: THREE.Vector3, readonly end: THREE.Vector3) { super(); }
    getPoint(t: number, target = new THREE.Vector3()): THREE.Vector3 {
        target.lerpVectors(this.start, this.end, t);
        target.y = this.start.y + (this.end.y - this.start.y) * t * t;
        return target;
    }
}
export function buildTownFountain(seed: number, time: {
    value: number;
}) {
    const rng = rngFromString(`town-fountain:v3:${seed}`), s = .95 + rng() * .3;
    const raised = rng() < .55 ? 1 : 2, spouts = rng() < .5 ? 4 : 6, phase = rng() * Math.PI * 2;
    const group = new THREE.Group();
    group.name = 'town-fountain';
    const basins: FountainBasin[] = [], flows: FountainFlow[] = [];
    const stone: THREE.BufferGeometry[] = [], pools: THREE.BufferGeometry[] = [], streams: THREE.BufferGeometry[] = [], ripples: THREE.BufferGeometry[] = [];
    const water = makePoolWaterMat(time), flowMat = waterMaterial(time, true);
    const stoneMat = new THREE.MeshStandardMaterial({ color: 0xb4aa91, roughness: .87 });
    const rippleMat = new THREE.MeshStandardMaterial({ color: 0xb8cfbd, roughness: .45, transparent: true, opacity: .24, depthWrite: false });
    const cylinder = (r: number, h: number, y: number) => new THREE.CylinderGeometry(r, r, h, 16).translate(0, y, 0);
    stone.push(cylinder(3.12 * s, .16, .08));
    let radius = 2.8 * s, baseY = .16;
    for (let i = 0; i <= raised; i++) {
        const basin = { radius, floor: baseY + .14, waterY: baseY + .34, rimY: baseY + .46 };
        basins.push(basin);
        stone.push(basinGeometry(radius, baseY));
        pools.push(new THREE.CircleGeometry(radius - .015, 48).rotateX(-Math.PI / 2).translate(0, basin.waterY, 0));
        if (i > 0) {
            const below = basins[i - 1], columnBottom = below.floor, columnTop = baseY + .05;
            stone.push(cylinder(.24 * s, columnTop - columnBottom, (columnTop + columnBottom) / 2));
            for (let j = 0; j < spouts; j++) {
                const a = phase + j * Math.PI * 2 / spouts + i * .35, d = new THREE.Vector3(Math.cos(a), 0, Math.sin(a));
                const start = d.clone().multiplyScalar(radius + .24);
                start.y = basin.waterY - .025;
                const end = d.clone().multiplyScalar(Math.min(below.radius - .24, radius + .65 * s));
                end.y = below.waterY + .008;
                flows.push({ from: i, to: i - 1, start, end });
                // The outlet crosses the bowl wall, joining the pool to the visible jet.
                const outlet = new THREE.CylinderGeometry(.09 * s, .09 * s, .38, 8);
                outlet.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d));
                outlet.translate(d.x * (radius + .065), start.y, d.z * (radius + .065));
                stone.push(outlet);
                streams.push(new THREE.TubeGeometry(new SpillCurve(start, end), 16, .043 * s, 5, false));
                for (const r of [.095, .2])
                    ripples.push(new THREE.RingGeometry(r * s, (r + .022) * s, 20)
                        .rotateX(-Math.PI / 2).translate(end.x, end.y + .002, end.z));
            }
        }
        radius *= i === 0 ? .54 : .51;
        baseY += 1.25 * s;
    }
    const top = basins[basins.length - 1];
    stone.push(cylinder(.18 * s, .7 * s, top.floor + .35 * s));
    stone.push(new THREE.SphereGeometry(.22 * s, 8, 6).translate(0, top.floor + .76 * s, 0));
    const geometries: THREE.BufferGeometry[] = [];
    for (const [parts, material, opaque] of [[stone, stoneMat, true], [pools, water, false], [streams, flowMat, false], [ripples, rippleMat, false]] as const) {
        const g = mergeGeometries(parts);
        parts.forEach(p => p.dispose());
        if (!g)
            throw new Error('Incompatible fountain geometry');
        geometries.push(g);
        const mesh = new THREE.Mesh(g, material);
        mesh.castShadow = opaque;
        mesh.receiveShadow = true;
        // Render impact rings after the pools at almost identical depths.
        if (parts === ripples)
            mesh.renderOrder = 1;
        group.add(mesh);
    }
    return { group, basins, flows, dispose() { geometries.forEach(g => g.dispose()); [stoneMat, water, flowMat, rippleMat].forEach(m => m.dispose()); group.removeFromParent(); } };
}
